import { FormBlock, FormCard } from '@qbe/ads-blocks-forms';
import { Box, Button, Spinner } from '@qbe/ads-core';
import React, { useEffect, useState } from 'react';

import Clauses from '~/components/Clauses';
import PartnerBanner from '~/components/PartnerBanner';
import PremiumPanel from '~/components/PremiumPanel';
import RemoveCoverModal from '~/components/RemoveCoverModal';
import ReviewBuildingAndContents from '~/components/ReviewBuildingAndContents';
import ReviewBusinessDetails from '~/components/ReviewBusinessDetails';
import ReviewCovers from '~/components/ReviewCovers';
import ReviewExcess from '~/components/ReviewExcess';
import ReviewExtraInfo from '~/components/ReviewExtraInfo';
import ReviewInterestedParties from '~/components/ReviewInterestedParties';
import ReviewLandlordName from '~/components/ReviewLandlordName';
import ReviewLiability from '~/components/ReviewLiability';
import ReviewMembership from '~/components/ReviewMembership';
import ERRORS from '~/constants/errors';
import { PAYMENTS_ROUTE } from '~/constants/routes';
import {
  useAppContent,
  useAppDispatch,
  useAppSelector,
  useBusinessDetails,
  usePolicyDataBuilder,
  useQuote,
  useSelectCover,
} from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useCoverOptions } from '~/hooks/useCoverOptions';
import { useReviewAndPay } from '~/hooks/useReviewAndPay';
import { useRTKQErrorHandler } from '~/hooks/useRTKQErrorHandler';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { useBreakpoint } from '~/hooks-ds/useBreakpoint';
import { selectIsReferral, selectIsReferralVelocity } from '~/selectors/selectAppDetails';
import { useSaveQuoteMutation, useUpdateQuoteMutation } from '~/services/quotes';
import { decline } from '~/slices/decline';
import { updateAccessToken, updateQuoteNumber } from '~/slices/quote';
import { resetAdditionalCovers } from '~/slices/reviewAndPay';
import {
  qalFormCompleteAction,
  qalFormStartAction,
  qalTrackEngagementAction,
} from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { Cover } from '~/types/cover';
import { PolicyBuilderMode } from '~/types/policy';
import { isNotEmptyObjOrArray } from '~/utils';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { CompletedCovers } from './CompletedCovers';
import { RemoveCoverButton } from './RemoveCoverButton';

const excludedFromCompletedCovers = [Cover.BuildingAndContents, Cover.Liability];

const Review: React.FC = () => {
  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });
  const { getTextContent } = useAppContent({ basePath: 'dsme.review' });

  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { premiums, quoteNumber, accessToken } = useQuote();
  const { isFloodZone } = useBusinessDetails();
  const { completedCovers, declinedCovers } = useSelectCover();
  const { additionalCovers } = useReviewAndPay();
  const removeCovers = [
    ...completedCovers,
    ...declinedCovers,
    ...((isFloodZone && [Cover.BuildingAndContents, Cover.BusinessInterruption]) || []),
  ];
  const { data: coverOptions } = useCoverOptions();
  const isReferral = useAppSelector(selectIsReferral);
  const hasMembership = useAppSelector(selectIsReferralVelocity);

  const additionalCoverOptions =
    coverOptions
      ?.filter((cover) => !removeCovers.includes(cover.id))
      .map((coverOption) => coverOption.id) ?? [];
  const onlyLiability = completedCovers.length === 1 && completedCovers.includes(Cover.Liability);
  const buildingAndContentsCompleted = completedCovers.includes(Cover.BuildingAndContents);
  const clausesExist = premiums?.policy?.risks?.some((risk) => !!risk.clauses);
  const [openCoverWarning, setCoverOpenWarning] = useState(false);
  const [removeCoverModalOpen, setRemoveCoverModalOpen] = useState(false);
  const [removableCovers, setRemovableCovers] = useState<Cover[]>([]);
  const [
    updateQuote,
    { data: updateQuoteData, error: updateQuoteError, isLoading: updateQuoteIsLoading },
  ] = useUpdateQuoteMutation();
  const [saveQuote, { data: saveQuoteData, error: saveQuoteError, isLoading: saveQuoteIsLoading }] =
    useSaveQuoteMutation();
  useRTKQErrorHandler(saveQuoteError ?? updateQuoteError);

  const { getPolicyData } = usePolicyDataBuilder({
    includeAccessToken: true,
    mode: PolicyBuilderMode.Quote,
  });

  const getPremiums = () => {
    const policyData = getPolicyData();
    if (quoteNumber && accessToken) {
      updateQuote({
        quoteNumber,
        policyData,
      });
    } else {
      saveQuote(policyData);
    }
  };

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  useEffect(() => {
    dispatch(resetAdditionalCovers());
  }, [dispatch]);

  useEffect(() => {
    const quoteData = saveQuoteData ?? updateQuoteData;
    if (quoteData) {
      const qNumber = quoteData?.policy?.policyHeader?.policyTransactionInformation?.policyNumber;
      const accToken = quoteData?.policy?.customerParty?.accessToken;
      if (!qNumber) {
        dispatch(decline(ERRORS.quoteNumberNotFound));
      } else if (!accToken) {
        dispatch(decline(ERRORS.accessTokenNotFound));
      } else {
        dispatch(updateQuoteNumber(qNumber));
        dispatch(updateAccessToken(accToken));
      }
      dispatch(
        navigateAction({
          to: PAYMENTS_ROUTE,
          navigate,
        })
      );
    }
  }, [dispatch, navigate, saveQuoteData, updateQuoteData]);

  const handleNext = (e: React.MouseEvent<HTMLElement>) => {
    handleFormStart();
    handleFormComplete();
    e.preventDefault();
    isNotEmptyObjOrArray(additionalCovers) ? setCoverOpenWarning(true) : getPremiums();
  };

  const onCloseRemoveCover = () => {
    setRemoveCoverModalOpen(false);
    dispatch(
      qalTrackEngagementAction({
        eventAction: 'modal: close',
        eventLabel: 'remove cover',
        data: {},
      })
    );
  };

  const removeCoversCallback = (coversToRemove: Cover[]) => {
    setRemoveCoverModalOpen(true);
    setRemovableCovers(coversToRemove);
  };

  const { isMobile } = useBreakpoint();

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  return (
    <>
      <Box // alternatively this could be added to PageLayout leftAside
        sx={
          isMobile
            ? {}
            : {
                position: 'fixed',
                top: '145px',
                marginLeft: '-910px',
                zIndex: 800,
              }
        }
      >
        <PremiumPanel
          onBuyNow={handleNext}
          isLoading={saveQuoteIsLoading || updateQuoteIsLoading}
        />
      </Box>
      <FormCard
        variant="pageTitle"
        title={getTextContent('title')}
        description={getTextContent('description')}
        customElement={isReferral ? <PartnerBanner type="quote" /> : null}
      />
      {isNotEmptyObjOrArray(additionalCoverOptions) && (
        <FormCard variant="content">
          <ReviewCovers
            options={additionalCoverOptions}
            openWarningModal={openCoverWarning}
            onClose={() => setCoverOpenWarning(false)}
            handleContinueWithoutAdding={() => {
              dispatch(resetAdditionalCovers());
              setCoverOpenWarning(false);
              getPremiums();
            }}
          />
        </FormCard>
      )}
      {clausesExist && (
        <FormCard variant="sectionTitle" title={getTextContent('fields.additionalClauses.title')}>
          <Clauses />
        </FormCard>
      )}
      <FormCard variant="content">
        <ReviewBusinessDetails />
      </FormCard>
      <FormCard variant="content">
        <ReviewLiability />
      </FormCard>
      {buildingAndContentsCompleted && (
        <FormCard variant="content">
          <ReviewBuildingAndContents />
          <RemoveCoverButton
            remove={removeCoversCallback}
            coversToRemove={[
              Cover.BuildingAndContents,
              ...(completedCovers.includes(Cover.BusinessInterruption)
                ? [Cover.BusinessInterruption]
                : []),
            ]}
          />
        </FormCard>
      )}
      <CompletedCovers
        remove={removeCoversCallback}
        completedCovers={completedCovers}
        excludedCovers={excludedFromCompletedCovers}
      />
      {hasMembership && (
        <FormCard variant="content">
          <ReviewMembership />
        </FormCard>
      )}
      <FormCard variant="content">
        <ReviewExtraInfo />
      </FormCard>
      <FormCard variant="content">
        <ReviewExcess />
      </FormCard>
      <FormCard variant="content">
        <ReviewLandlordName />
      </FormCard>
      {!onlyLiability && (
        <FormCard variant="content">
          <ReviewInterestedParties />
        </FormCard>
      )}
      <FormCard variant="content">
        <Spinner
          loading={saveQuoteIsLoading || updateQuoteIsLoading}
          description={getSharedContent('loadingLabel')}
        />
        <FormBlock preset="button">
          <Button
            label={getTextContent('buttons.buyNow')}
            data-track="buy now - after add cover"
            onClick={handleNext}
            disabled={saveQuoteIsLoading || updateQuoteIsLoading}
          />
        </FormBlock>
      </FormCard>
      {removeCoverModalOpen && (
        <RemoveCoverModal
          covers={removableCovers}
          onCancel={onCloseRemoveCover}
          onRemove={onCloseRemoveCover}
        />
      )}
    </>
  );
};

export default Review;
